import { Injectable, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of } from 'rxjs';
import { delay, filter, first, map, switchMap, take, tap } from 'rxjs/operators';

import { isOptionSymbol } from '@easy-lib/tools';
import lzString from 'lz-string';
import { SearchCategory } from '../../../modules/search/models/search-symbol.model';
import {
	IManner,
	ISymbol,
	ISymbolResponse,
	ISymbolWithCategory,
	SectorList,
	SectorListResponse
} from '../../../modules/shared/models/symbol.model';
import { BaseService } from '../../../modules/shared/services/Base/base.service';
import { normalizeTitle, remove1FormEndOfSymbolName } from '../../../tools/normalize-title';
import { UnleashFeatures } from '../models/unleash.model';
import { sKey } from './local-storage.service';
import { UnleashService } from './unleash.service';

@Injectable({
	providedIn: 'root'
})
export class SymbolService extends BaseService {
	unleashService = inject(UnleashService);
	private _symbols: ISymbol[];
	get symbols(): ISymbol[] {
		return this._symbols;
	}

	set symbols(value: ISymbol[]) {
		this._symbols = value;
		if (value) this.symbolsSubject.next(value);
	}

	private symbolsSubject = new BehaviorSubject<ISymbol[]>(this.symbols);
	symbols$ = this.symbolsSubject.asObservable();
	private notNullSymbols$ = this.symbols$.pipe(filter(symbols => !!symbols && !!symbols.length));

	private mannersSubject = new BehaviorSubject<IManner[]>(this.manners);
	manners$ = this.mannersSubject.asObservable();

	subscribedSymbols = [];

	private _manners: IManner[];
	get manners(): IManner[] {
		return this._manners;
	}

	set manners(value: IManner[]) {
		this._manners = value;
		if (value) this.mannersSubject.next(value);
	}

	defaultSymbols: ISymbol[] = [];

	constructor() {
		super();
		this.fillSymbols();
	}

	private awaiter = new BehaviorSubject<boolean>(false);
	symbolIsRecieved$ = this.awaiter.pipe(first(Boolean));

	// when you want to set symbol in getter
	symbolByIsin: Record<string, Partial<ISymbol>>;
	symbolByName: Record<string, Partial<ISymbol>>;
	symbolByCompanyIsin: Record<string, Partial<ISymbol>>;
	// when you want to use in Computed
	symbolByIsinSignal = signal<Record<string, Partial<ISymbol>>>(null);

	OptionSymbols$ = this.symbolIsRecieved$.pipe(
		switchMap(() => this.symbols$),
		map(symbols => symbols.filter(symbol => isOptionSymbol(symbol.marketUnit)))
	);
	OptionBaseIsins$ = this.OptionSymbols$.pipe(
		map(symbols => symbols.map(symbol => symbol.baseIsin).filter(Boolean)),
		map(isins => [...new Set(isins)])
	);
	OptionBaseSymbols$ = this.OptionBaseIsins$.pipe(
		map(baseIsins => baseIsins.map(baseIsin => this.symbolByCompanyIsin?.[baseIsin]))
	);

	private fillSymbols() {
		this.notNullSymbols$.subscribe(symbols => {
			if (symbols) {
				this.symbolByIsin = Object.fromEntries(symbols?.map(symbol => [symbol.symbolIsin, symbol]));
				this.symbolByName = Object.fromEntries(symbols?.map(symbol => [symbol.symbolName, symbol]));
				this.symbolByCompanyIsin = Object.fromEntries(symbols?.map(symbol => [symbol.companyIsin, symbol]));
				this.symbolByIsinSignal.set(this.symbolByIsin);
				this.awaiter.next(true);
			}
		});
	}

	// When you want to set symbol for isin once
	async getSymbol(isin) {
		await firstValueFrom(this.awaiter.pipe(first(res => res)));
		return this.symbolByIsin[isin];
	}

	// When you want to set symbol for isin by symbol changes
	getSymbolStream(isin) {
		return this.notNullSymbols$.pipe(
			map(symbols => symbols.find(x => isin && x.symbolIsin?.toUpperCase() === isin?.toUpperCase()))
		);
	}

	getOptionBaseSymbolStream(baseIsin) {
		return this.notNullSymbols$.pipe(
			map(symbols => symbols.find(x => baseIsin && x.companyIsin?.toUpperCase() === baseIsin?.toUpperCase()))
		);
	}

	isOptionParentSymbol(symbolIsin: string) {
		return this.notNullSymbols$.pipe(
			map(symbols => {
				const symbol = symbols.find(symbol => symbol.symbolIsin === symbolIsin);
				return symbols.some(a => symbol && a.baseIsin === symbol?.companyIsin);
			})
		);
	}
	async getSymbols(optionSymbols = false): Promise<ISymbol[]> {
		if (optionSymbols) return await firstValueFrom(this.OptionBaseSymbols$ as Observable<ISymbol[]>);
		return await firstValueFrom(this.notNullSymbols$);
	}

	getSymbolsStream() {
		return this.notNullSymbols$;
	}
	groupSymbolsByCategory(inputSymbols: ISymbolWithCategory[]): Map<SearchCategory, ISymbolWithCategory[]> {
		const resultSymbol = new Map<SearchCategory, ISymbolWithCategory[]>();
		inputSymbols.forEach(val => {
			if (!resultSymbol.has(val.searchCategoryName)) {
				resultSymbol.set(val.searchCategoryName, []);
			}
			const targetArray: ISymbolWithCategory[] = resultSymbol.get(val.searchCategoryName);
			targetArray.push(val);
			resultSymbol.set(val.searchCategoryName, targetArray);
		});

		const sortedMapResult = new Map<SearchCategory, ISymbolWithCategory[]>();
		for (const category of Object.values(SearchCategory)) {
			if (resultSymbol.get(category as SearchCategory)) {
				sortedMapResult.set(category as SearchCategory, resultSymbol.get(category as SearchCategory));
			}
		}
		return sortedMapResult;
	}

	getSameSymbols(isin: string, sector: string): ISymbolWithCategory[] {
		const sameSymbols =
			this.symbols
				?.filter(x => x.sector === sector && x.symbolIsin !== isin)
				.map(
					x => ({ ...x, searchCategoryName: this.getCategoryByIsin(x.symbolIsin) }) as ISymbolWithCategory
				) ?? [];

		return sameSymbols;
	}

	getSameSymbolsAsObservable(isin: string, sector: string): Observable<ISymbolWithCategory[]> {
		const sameSymbols =
			this.symbols
				?.filter(x => x.sector === sector && x.symbolIsin !== isin)
				.map(
					x => ({ ...x, searchCategoryName: this.getCategoryByIsin(x.symbolIsin) }) as ISymbolWithCategory
				) ?? [];

		return of(sameSymbols);
	}

	checkSymbolsOnLoad() {
		this.checkMannersOnLoad();
		localStorage.removeItem('symbols');
		try {
			const compressedData = localStorage.getItem(sKey.symbol);
			if (compressedData) {
				const json = lzString.decompress(compressedData);
				const res: SymbolResult = JSON.parse(json);
				this.resetSymbols(res.symbols);
				this.unleashService.isFeatureEnabled$(UnleashFeatures.option).subscribe(isEnabled => {
					if (isEnabled !== res.hasOption) this.getAllFromRemote('null');
					else this.getAllFromRemote(res.hash);
				});
			} else {
				this.getAllFromRemote('null');
			}
		} catch (error) {
			this.getAllFromRemote('null');
		}
	}

	checkMannersOnLoad(): void {
		const json = localStorage.getItem('manners');
		if (json) {
			const res: IManner[] = JSON.parse(json);
			this.manners = res;
		}
		this.getManners();
	}

	private getManners(): any {
		return this.httpService
			.get<IManner[]>(this.mtsPath + this.apiUrls.symbol + 'symbols/manners')
			.pipe(
				tap(res => {
					if (res) {
						localStorage.setItem('manners', JSON.stringify(res));
						this.manners = res;
					}
				})
			)
			.subscribe();
	}

	private getAllFromRemote(hash: string): any {
		return this.httpService
			.post<ISymbolResponse>(this.mtsPath + this.apiUrls.symbol + 'symbols/all', {
				body: { hash },
				maxRetryAttempts: 2
			})
			.pipe(
				take(1),
				tap(res => {
					if (res?.symbols?.length > 0) {
						res.symbols.forEach(element => {
							element.symbolName = remove1FormEndOfSymbolName(element.symbolName);
							const normalizedTitle = normalizeTitle(element.symbolName);
							if (normalizedTitle !== element.symbolName) {
								element.searchOptimizeTitle = normalizedTitle;
							}
						});

						this.unleashService.isFeatureEnabled$(UnleashFeatures.option).subscribe(isEnabled => {
							res.hasOption = isEnabled;
							const compressed = lzString.compress(JSON.stringify(res));
							localStorage.setItem(sKey.symbol, compressed);
						});

						this.resetSymbols(res.symbols);
						return of(res.symbols);
					} else {
						if (this.symbols) {
							return of(this.symbols);
						} else {
							const jsonSymbols = localStorage.getItem(sKey.symbol);
							if (jsonSymbols) {
								const json = lzString.decompress(jsonSymbols);
								const result: SymbolResult = JSON.parse(json);
								this.resetSymbols(res.symbols);
								return of(result);
							}
						}
					}
				})
			)
			.subscribe();
	}

	updateSymbolIsin(isins: string[]) {
		const symbolIsins = this.symbolsSubject.value?.map(t => t.symbolIsin);
		const deActiveSymbols = isins.filter(isin => !symbolIsins.includes(isin));
		if (deActiveSymbols?.length) this.getSome(deActiveSymbols).subscribe();
	}
	public getSome(isins: string[]): Observable<{ symbols: ISymbol[] }> {
		return this.httpService
			.post<{ symbols: ISymbol[] }>(this.mtsPath + this.apiUrls.symbol + 'symbols/some', { body: { isins } })
			.pipe(
				take(1),
				tap(res => {
					if (res && res?.symbols && res?.symbols?.length > 0) {
						const jsonSymbols = localStorage.getItem(sKey.symbol);
						if (jsonSymbols) {
							const json = lzString.decompress(jsonSymbols);
							const result: SymbolResult = JSON.parse(json);
							if (result) {
								result.symbols = this.uniqueBy(
									[...(result?.symbols ?? []), ...(res?.symbols ?? [])],
									'symbolIsin'
								);
								const compressed = lzString.compress(JSON.stringify(result));
								localStorage.setItem(sKey.symbol, compressed);
								this.resetSymbols(result.symbols);
							}
						}

						return of(res.symbols);
					}
				})
			);
	}

	private uniqueBy(arr: ISymbol[], prop: string) {
		return arr.reduce((filteredSymbols: ISymbol[], symbol: ISymbol) => {
			const index = filteredSymbols.findIndex(item => item[prop] === symbol[prop]);
			if (index === -1) filteredSymbols.push(symbol);
			return filteredSymbols;
		}, []);
	}

	sectors(): Observable<SectorList> {
		const sectors = localStorage.getItem('sectors');
		const url = `${this.mtsPath}${this.apiUrls.symbol}sectors/sieve`;
		const delayTime = sectors ? 10000 : 0;
		const body = { page: 0, pageSize: 1000 };
		return this.httpService.post<SectorListResponse>(url, { body }).pipe(
			delay(delayTime),
			map(data => data?.records as SectorList),
			tap(sectors => localStorage.setItem('sectors', JSON.stringify(sectors)))
		);
	}

	calculateLastTradedPriceVar(lastTradedPrice?: number, feeOfPreviousDaysClosingPrice?: number): number {
		return lastTradedPrice - (feeOfPreviousDaysClosingPrice ?? 0);
	}

	calculateClosingPriceVarPercent(
		closePrice?: number,
		feeOfPreviousDaysClosingPrice?: number,
		totalNumberOfSharesTraded?: number
	): number {
		if (
			feeOfPreviousDaysClosingPrice === 0 ||
			!feeOfPreviousDaysClosingPrice ||
			!totalNumberOfSharesTraded ||
			totalNumberOfSharesTraded === 0
		)
			return 0;

		if (closePrice > 0 && feeOfPreviousDaysClosingPrice > 0)
			return (
				Math.round(((closePrice - feeOfPreviousDaysClosingPrice) / feeOfPreviousDaysClosingPrice) * 10000) / 100
			);
		return 0;
	}
	private resetSymbols(symbols: ISymbol[]) {
		this.defaultSymbols = symbols.filter(symbol =>
			['IRT3SATF0001', 'IRTKMOFD0001', 'IRT1ARAM0001', 'IRT3AVNF0001', 'IRT1STIL0001', 'IRT3TVAF0001'].includes(
				symbol.symbolIsin
			)
		);
		this.symbols = symbols;
	}

	getCategoryByIsin(isin: string) {
		const stocks = ['IRO1', 'IRO3', 'IRO7', 'IRO8'];
		const precedence = ['IRR1', 'IRR3', 'IRR5', 'IRR7'];
		const etf = ['IRT1', 'IRT3', 'IRTE', 'IRTK'];
		const commodityDeposit = ['IRK1'];
		const housingBonds = ['IRO6'];
		const debtSecurities = ['IRB1', 'IRB3', 'IRB4', 'IRB5', 'IRB6', 'IRB8'];
		const subsidiaryPapers = ['IRS4', 'IRS5', 'IRSA'];
		const callOption = ['IRO9', 'IROA'];
		const putOption = ['IROF', 'IROB'];
		// const rest = ['IRBE', 'IRBK', 'IRBZ', 'IRE9', 'IRI3', 'IRO2', 'IRO4', 'IRO5', 'IRO9', 'IROF', 'IRR5'];

		if (stocks.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.Stock;
		} else if (precedence.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.Precedence;
		} else if (etf.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.ETF;
		} else if (commodityDeposit.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.CommodityDeposit;
		} else if (housingBonds.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.HousingBonds;
		} else if (debtSecurities.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.DebtPapers;
		} else if (subsidiaryPapers.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.SubsidiaryPapers;
		} else if (callOption.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.callOption;
		} else if (putOption.indexOf(isin.substring(0, 4)) >= 0) {
			return SearchCategory.putOption;
		} else {
			return SearchCategory.Rest;
		}
	}

	async setSymbol(arr: any[]) {
		const newOrders = [];
		for (const item of arr) {
			const symbol = await this.getSymbol(item.symbolIsin);
			newOrders.push({ ...item, symbolName: symbol?.symbolName });
		}
		return newOrders;
		//this.ipoService.getIpos()?.find(t => t.symbolIsin === o.symbolIsin)?.symbolName;
	}
}

export class SymbolResult {
	symbols: ISymbol[];
	hash: string;
	hasOption: boolean;
}
